import { makeStyles, Card, Typography, Button, ActionButton, Spinner, IconButton, Hidden, Link } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import { EstimatorAvatar, ProgressBar, useClientOptions, useUsers } from '@paintscout/ui-business'
import type { CalendarEvent } from 'paintscout'
import AddIcon from '@material-ui/icons/Add'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import React from 'react'
import moment from 'moment'
import { getCrews, getTimezone } from '@paintscout/util/builder'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'

const DATE_FORMAT = 'MMM DD, YYYY'

const useStyles = makeStyles<Theme, ProductionEventDocProps>(
  (theme) => ({
    root: {
      [theme.breakpoints.down('sm')]: {
        border: 'none'
      }
    },
    actions: {
      minWidth: '110px'
    },
    titleContainer: (props) => ({
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      width: '100%',
      alignItems: 'center',
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      minHeight: props.hideTitle ? 0 : '3.5rem',
      paddingRight: theme.spacing(0.5),
      paddingLeft: theme.spacing(2)
    }),
    titleSpacer: {
      minWidth: '1px'
    },
    topDateDiv: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(110px, max-content))',
      columnGap: theme.spacing(4),
      rowGap: theme.spacing(0.5),
      justifyContent: 'flex-start',
      alignItems: 'start'
    },
    noEventBody: {
      padding: theme.spacing(5),
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center'
    },
    eventBody: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2)
    },
    progressDiv: {
      paddingRight: theme.spacing(1)
    },
    dateDiv: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(110px, max-content))',
      columnGap: theme.spacing(4),
      rowGap: theme.spacing(1),
      justifyContent: 'flex-start',
      alignItems: 'start'
    },
    crewDiv: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1)
    },
    crewData: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing(1.25)
    },
    crewBlobWrap: {
      display: 'inline-flex',
      alignItems: 'center',
      height: '1.25rem',
      flexShrink: 0
    },
    crewBlob: {
      display: 'inline-block',
      width: 22,
      height: 10,
      borderRadius: 999,
      flexShrink: 0
    },
    crewName: {
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    button: {
      marginBottom: theme.spacing(2),
      marginTop: `-${theme.spacing(1)}px`
    }
  }),
  { name: 'ProductionEventDoc' }
)

export interface ProductionEventDocProps {
  classes?: StyleClasses<typeof useStyles>
  event?: CalendarEvent
  loading?: boolean
  hideNew?: boolean
  hideTitle?: boolean
  handleCreate: () => any
  handleEdit: (event: CalendarEvent) => any
  handleCancel: (event: CalendarEvent) => any
  handleUpdate: (event: CalendarEvent) => any
}

function ProductionEventDoc(props: ProductionEventDocProps) {
  const classes = useStyles(props)
  const { options } = useClientOptions()
  const { userPictures, users } = useUsers()
  const { event, loading, hideNew, hideTitle = false, handleCreate, handleEdit, handleCancel, handleUpdate } = props
  const [sliderValue, setSliderValue] = React.useState(event?.progress ?? 0)
  React.useEffect(() => {
    setSliderValue(event?.progress ?? 0)
  }, [event?.progress])
  const crews = getCrews({ options })
  const crew = event?.crew ? crews.find((c) => c._id === event.crew) : undefined
  const pmUser = users?.find((u) => u.user_id === crew?.projectManager?.id)
  const hasCrews = crews.length > 0
  const handleAction = (action: string) => {
    if (action === 'edit') {
      handleEdit(event)
    } else if (action === 'cancel') {
      handleCancel(event)
    }
  }
  const timezone = getTimezone({ options })

  return (
    <Card className={classes.root} data-testid={'quote-list'}>
      <div className={classes.titleContainer}>
        {event ? (
          <>
            <Hidden smDown>
              {!hideTitle ? <Typography variant="h3">Schedule</Typography> : <div className={classes.titleSpacer} />}
            </Hidden>
            <Hidden mdUp>
              <div className={classes.topDateDiv}>
                <div>
                  <Typography variant="body2" color="textSecondary">
                    Start Date
                  </Typography>
                  <Typography variant="body1">
                    {event.start ? moment(event.start).tz(timezone).format(DATE_FORMAT) : 'No Start Date'}
                  </Typography>
                </div>
                <div>
                  <Typography variant="body2" color="textSecondary">
                    End Date
                  </Typography>
                  <Typography variant="body1">
                    {event.end ? moment(event.end).tz(timezone).format(DATE_FORMAT) : 'No End Date'}
                  </Typography>
                </div>
              </div>
            </Hidden>
          </>
        ) : (
          !hideTitle && <Typography variant="h3">Schedule</Typography>
        )}
        {!event ? (
          hideNew ? null : (
            <Button
              variant="hoverUnderline"
              color="primary"
              loading={loading}
              icon={AddIcon}
              onClick={handleCreate}
              className="tour-event-table-new-button"
              disabled={loading || !hasCrews}
            >
              New
            </Button>
          )
        ) : (
          <ActionButton
            classes={{ menuItem: classes.actions }}
            data-testid="actions"
            disabled={loading}
            size="medium"
            actions={[
              {
                label: 'Edit',
                key: 'edit'
              },
              {
                key: 'divider',
                label: 'divider'
              },
              {
                label: 'Delete',
                key: 'cancel',
                scary: true
              }
            ]}
            onActionClick={(ev, action) => handleAction(action)}
            button={
              <IconButton color="inherit" disableRipple aria-label="Schedule actions" id="action-button-icon">
                <MoreVertIcon />
              </IconButton>
            }
          />
        )}
      </div>
      {loading ? (
        <div className={classes.noEventBody}>
          <Spinner />
        </div>
      ) : !event ? (
        <div className={classes.noEventBody}>
          <Typography variant="subtitle1">
            {hasCrews ? (
              'No job found.'
            ) : (
              <>
                Create a crew in <Link to={'/settings?section=billing-users&outerTab=crew'}>User Management</Link> to
                schedule this job.
              </>
            )}
          </Typography>
        </div>
      ) : (
        <div className={classes.eventBody}>
          <Hidden smDown>
            <div className={classes.dateDiv}>
              <div>
                <Typography variant="body2" color="textSecondary">
                  Start Date
                </Typography>
                <Typography variant="body1">
                  {event.start ? moment(event.start).tz(timezone).format(DATE_FORMAT) : 'No Start Date'}
                </Typography>
              </div>
              <div>
                <Typography variant="body2" color="textSecondary">
                  End Date
                </Typography>
                <Typography variant="body1">
                  {event.end ? moment(event.end).tz(timezone).format(DATE_FORMAT) : 'No End Date'}
                </Typography>
              </div>
            </div>
          </Hidden>
          <div className={classes.crewDiv}>
            <Typography variant="body2" color="textSecondary">
              Crew
            </Typography>
            <div className={classes.crewData}>
              <span className={classes.crewBlobWrap}>
                <span className={classes.crewBlob} style={{ backgroundColor: crew?.color ?? '#cbd5e1' }} />
              </span>
              <Typography variant="body1" className={classes.crewName}>
                {crew?.name ?? 'No Crew'}
              </Typography>
              {crew?.projectManager?.id && (
                <EstimatorAvatar
                  user={{
                    id: crew.projectManager.id,
                    firstName: crew.projectManager.firstName,
                    lastName: crew.projectManager.lastName
                  }}
                  userPictures={userPictures ?? {}}
                  borderColor={pmUser?.user_metadata?.color}
                />
              )}
            </div>
          </div>
          <div className={classes.progressDiv}>
            <ProgressBar
              value={Math.max(0, Math.min(100, sliderValue))}
              color={crew?.color}
              disablePadding
              onChange={(next) => {
                setSliderValue(next)
                handleUpdate({
                  ...event,
                  progress: next,
                  previousProgress: event.progress
                })
              }}
            />
          </div>
          <Button
            variant="eventComplete"
            className={classes.button}
            active={sliderValue === 100}
            fullWidth
            startIcon={<CheckCircleIcon />}
            onClick={() => {
              if (sliderValue === 100) {
                setSliderValue(event.previousProgress || 0)
                handleUpdate({
                  ...event,
                  progress: event.previousProgress || 0,
                  previousProgress: event.progress
                })
              } else {
                handleUpdate({
                  ...event,
                  progress: 100,
                  previousProgress: event.progress
                })
                setSliderValue(100)
              }
            }}
          >
            {sliderValue === 100 ? 'Job Complete' : 'Mark Job Complete'}
          </Button>
        </div>
      )}
    </Card>
  )
}

export default React.memo(ProductionEventDoc)
